import { get as lodashGet } from 'lodash'
import { KV_LIVE_STATS_UNLEASH_FLAG } from '@ahaslides-product/frontend-toolkit'
import { slideType } from '@/constant/allSlideTypes'
import SlideApi from '@/api/slide'
import { CATEGORY_OPTION_LENGTH } from '@/constant/Categorise.constant'
import {
  STATS_TYPES, AGGREGATION_LEVELS,
} from '@/mqtt/liveStats'
import config from '@/config'

const LIVESTAT_SUBSCRIBE_STATS_ACTION = 'liveStatsV2/subscribeStatsAction'
const LIVESTAT_UNSUBSCRIBE_STATS_ACTION = 'liveStatsV2/unsubscribeStatsAction'
const KV_SYNC_SUBSCRIBE_KEY_ACTION = 'kvSync/subscribeKeyAction'
const KV_SYNC_UNSUBSCRIBE_KEY_ACTION = 'kvSync/unsubscribeKeyAction'
const RETIRE_LIVE_API_UNLEASH_FLAG = 'liveStatsV2/canRetireLiveApi'

const services = {
  updateCategories: async (slideId, categories) => {
    await SlideApi.patchCategoriesBySlideId(slideId, { categories })
  },
  generateCategory: () => ({ id: window.crypto.randomUUID(), value: '', options: [] }),
}

const _getters = {
  categoriseSlides: (_, __, rootState) => {
    const slides = lodashGet(rootState, 'presentation.slides', [])
    return slides.filter((slide) => slide.type === slideType.categorise)
  },
  slideActiveCategories: (_, __, rootState) => {
    const defaultCategories = JSON.stringify([
      services.generateCategory(),
      services.generateCategory(),
    ])
    const categoryRawData = lodashGet(rootState, 'slideActive.matchingQuestionOptions', defaultCategories) || defaultCategories
    const categories = JSON.parse(categoryRawData)
    if (categories.length === 0) {
      return [services.generateCategory(), services.generateCategory()]
    }

    if (categories.length === 1) {
      return [...categories, { ...services.generateCategory() }]
    }
    return categories
  },

}

const makeCategoriseV2SubscribeKey = ({
  versionPrefix,
  presentationId, slideId, slideVersion, statsType, aggregationLevel, questionId, answerValue,
}) => {
  const key = `/${versionPrefix}/${config.livestatsTopicPrefix}/counting/presentation-${presentationId}/categorise-v2/${statsType}/${aggregationLevel}/${slideId}/${slideVersion}`
  if (questionId != undefined && answerValue != undefined) {
    return `${key}/${questionId}/${answerValue}`
  }
  return key
}

export const makeAnswerValues = (matchingQuestionOptions) => {
  const categories = JSON.parse(matchingQuestionOptions)
  const answerValues = []
  categories?.forEach((category) => {
    category.options?.forEach((option) => {
      answerValues.push(encodeURIComponent(`${category.value}*|*${option.value}`))
    })
  })
  return answerValues
}

const actions = {
  async subscribeCategoriseStatsV2Action({ dispatch, rootGetters }, { presentation, slide }) {
    if (!rootGetters[KV_LIVE_STATS_UNLEASH_FLAG]) {
      return null
    }
    const answerValues = makeAnswerValues(slide.matchingQuestionOptions)

    // Correct/incorrect count for activity level (slide)
    const statsTypes = [
      STATS_TYPES.CORRECT_ANSWER_COUNT,
      STATS_TYPES.INCORRECT_ANSWER_COUNT,
    ]
    const statsKeys = []
    statsTypes.forEach((statsType) => {
      statsKeys.push(
        makeCategoriseV2SubscribeKey({
          versionPrefix: rootGetters['liveStatsV2/liveStatsVersionPrefix'],
          presentationId: presentation.id,
          slideId: slide.id,
          slideVersion: slide.version,
          statsType,
          aggregationLevel: AGGREGATION_LEVELS.PER_ACTIVITY,
        }),
      )
      answerValues.forEach((answerValue) => {
        statsKeys.push(
          makeCategoriseV2SubscribeKey({
            versionPrefix: rootGetters['liveStatsV2/liveStatsVersionPrefix'],
            presentationId: presentation.id,
            slideId: slide.id,
            slideVersion: slide.version,
            statsType,
            aggregationLevel: AGGREGATION_LEVELS.PER_ANSWER_VALUE,
            questionId: 0,
            answerValue,
          }),
        )
      })
    })

    const statsSubscriptions = statsKeys.map((statsKey) => {
      return dispatch(KV_SYNC_SUBSCRIBE_KEY_ACTION, {
        key: statsKey,
      }, { root: true })
    })
    await Promise.all(statsSubscriptions)

    // also, fetch the stats that produced by live-api to keep the old categorise slides work
    await dispatch('liveStatsV2/fetchStatsByKeyAction', {
      statsKeys: [
        `${STATS_TYPES.CORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${slide.id}/${slide.version}`,
        `${STATS_TYPES.INCORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${slide.id}/${slide.version}`,
      ],
    }, { root: true })

    await dispatch('liveStatsV2/fetchStatsByWildCardAction', {
      path: `${STATS_TYPES.CORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${slide.id}/${slide.version}`,
    }, { root: true })

    await dispatch('liveStatsV2/fetchStatsByWildCardAction', {
      path: `${STATS_TYPES.INCORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${slide.id}/${slide.version}`,
    }, { root: true })

    return async function unsubscribeFn() {
      const unsubscribeCalls = statsKeys.map((statsKey) => {
        return dispatch(KV_SYNC_UNSUBSCRIBE_KEY_ACTION, statsKey, { root: true })
      })
      await Promise.all(unsubscribeCalls)
    }
  },

  async subscribeCategoriseStatsAction({ dispatch, rootGetters, rootState }, slide) {
    if (rootGetters[RETIRE_LIVE_API_UNLEASH_FLAG]) {
      return dispatch('subscribeCategoriseStatsV2Action', { presentation: rootState.presentation, slide })
    }

    if (rootGetters[KV_LIVE_STATS_UNLEASH_FLAG]) {
      await dispatch('liveStatsV2/fetchStatsByKeyAction', {
        statsKeys: [
          `${STATS_TYPES.CORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${slide.id}/${slide.version}`,
          `${STATS_TYPES.INCORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ACTIVITY}/${slide.id}/${slide.version}`,
        ],
      }, { root: true })

      await dispatch('liveStatsV2/fetchStatsByWildCardAction', {
        path: `${STATS_TYPES.CORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${slide.id}/${slide.version}`,
      }, { root: true })

      await dispatch('liveStatsV2/fetchStatsByWildCardAction', {
        path: `${STATS_TYPES.INCORRECT_ANSWER_COUNT}/${AGGREGATION_LEVELS.PER_ANSWER_VALUE}/${slide.id}/${slide.version}`,
      }, { root: true })
    }

    dispatch(LIVESTAT_SUBSCRIBE_STATS_ACTION, {
      identifier: slide.id,
      statsType: STATS_TYPES.CORRECT_ANSWER_COUNT,
      aggregationLevel: AGGREGATION_LEVELS.PER_ACTIVITY,
    }, { root: true })

    dispatch(LIVESTAT_SUBSCRIBE_STATS_ACTION, {
      identifier: slide.id,
      statsType: STATS_TYPES.INCORRECT_ANSWER_COUNT,
      aggregationLevel: AGGREGATION_LEVELS.PER_ACTIVITY,
    }, { root: true })

    dispatch(LIVESTAT_SUBSCRIBE_STATS_ACTION, {
      identifier: slide.id,
      statsType: STATS_TYPES.CORRECT_ANSWER_COUNT,
      aggregationLevel: AGGREGATION_LEVELS.PER_ANSWER_VALUE,
    }, { root: true })

    dispatch(LIVESTAT_SUBSCRIBE_STATS_ACTION, {
      identifier: slide.id,
      statsType: STATS_TYPES.INCORRECT_ANSWER_COUNT,
      aggregationLevel: AGGREGATION_LEVELS.PER_ANSWER_VALUE,
    }, { root: true })

    const slideId = slide.id
    return function unsubscribeFn() {
      dispatch('unsubscribeCategoriseStatsAction', { slideId })
    }
  },

  unsubscribeCategoriseStatsAction({ dispatch }, { slideId }) {
    const subscriptions = [
      {
        statsType: STATS_TYPES.CORRECT_ANSWER_COUNT,
        aggregationLevel: AGGREGATION_LEVELS.PER_ACTIVITY,
      },
      {
        statsType: STATS_TYPES.INCORRECT_ANSWER_COUNT,
        aggregationLevel: AGGREGATION_LEVELS.PER_ACTIVITY,
      },
      {
        statsType: STATS_TYPES.CORRECT_ANSWER_COUNT,
        aggregationLevel: AGGREGATION_LEVELS.PER_ANSWER_VALUE,
      },
      {
        statsType: STATS_TYPES.INCORRECT_ANSWER_COUNT,
        aggregationLevel: AGGREGATION_LEVELS.PER_ANSWER_VALUE,
      },
    ]

    subscriptions.forEach((sub) => {
      dispatch(LIVESTAT_UNSUBSCRIBE_STATS_ACTION, {
        identifier: slideId,
        ...sub,
      }, { root: true })
    })
  },

  async updateCategoriesAction({ commit, rootState }, payload) {
    const { slideActive } = rootState
    if (!slideActive) return
    const matchingQuestionOptions = JSON.stringify(payload)
    const nonEmptyCategories = payload.filter((category) => category && category.value && category.value.trim())
    if (nonEmptyCategories && nonEmptyCategories.length > 0) {
      nonEmptyCategories.forEach((category) => {
        category.options = category.options.filter(
          (option) => option.value && option.value.trim().length <= CATEGORY_OPTION_LENGTH,
        )
      })
    }
    commit('updateSlideActive', { matchingQuestionOptions }, { root: true })
    await services.updateCategories(slideActive.id, nonEmptyCategories)
  },
}

const mutations = {

}

const plugins = {
  // This adapter is for redirecting the mutation to set categorise counting stats to the old liveStatsV2 state, so the UI component remain the same.
  countingMessageAdapter: (_store) => {
    _store.subscribeAction((action) => {
      if (action.type === 'kvSync/updateKeyValueAction') {
        const { key, value } = action.payload
        if (!key.includes('categorise-v2')) { return }
        // transform the key
        // from: `/ST1/livestats-local/counting/presentation-1/categorise-v2/<the rest>`
        // to: `<the rest>`
        const match = key.match(/categorise-v2\/(.*)$/)
        const statsKey = match ? match[1] : ''
        let valueObj
        try {
          valueObj = JSON.parse(value)
        } catch (e) {
          // Invalid JSON, ignore.
          return
        }
        const statsValue = valueObj.total
        if (!statsKey || statsValue === undefined) { return }
        _store.commit('liveStatsV2/updateStats', {
          topic: `categorise-v2/${decodeURIComponent(statsKey)}`,
          message: statsValue,
        }, { root: true })
      }
    })
  },
}

const categorise = {
  namespaced: true,
  getters: _getters,
  actions,
  mutations,
  plugins: [
    plugins.countingMessageAdapter,
  ],
}

export default categorise
